
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Award, Edit, Trash2 } from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface SkillsTabProps {
  userId: string;
}

const SkillsTab: React.FC<SkillsTabProps> = ({ userId }) => {
  const { toast } = useToast();
  const [userSkills, setUserSkills] = useState([]);
  const [newSkillName, setNewSkillName] = useState('');
  const [proficiencyLevel, setProficiencyLevel] = useState('');
  const [skillYearsExperience, setSkillYearsExperience] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [currentSkill, setCurrentSkill] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchUserSkills();
    }
  }, [userId]);

  const fetchUserSkills = async () => {
    try {
      const { data, error } = await supabase
        .from('job_seeker_skills')
        .select(`
          id,
          proficiency_level,
          years_of_experience,
          skill_name
        `)
        .eq('job_seeker_id', userId);

      if (error) throw error;
      setUserSkills(data || []);
    } catch (error) {
      console.error('Error fetching user skills:', error.message);
    }
  };

  const resetForm = () => {
    setNewSkillName('');
    setProficiencyLevel('');
    setSkillYearsExperience('');
    setEditMode(false);
    setCurrentSkill(null);
  };

  const handleAddSkill = async () => {
    if (!userId) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
      });
      return;
    }
    
    if (!newSkillName) {
      toast({
        title: "Required fields missing",
        description: "Please enter a skill name.",
        variant: "destructive",
      });
      return;
    }
    
    if (!proficiencyLevel) {
      toast({
        title: "Required fields missing",
        description: "Proficiency level is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('job_seeker_skills')
        .insert({
          job_seeker_id: userId,
          skill_name: newSkillName,
          proficiency_level: proficiencyLevel,
          years_of_experience: parseInt(skillYearsExperience) || 0,
        });

      if (error) throw error;

      resetForm();
      fetchUserSkills();

      toast({
        title: "Skill added",
        description: "Your skill has been successfully added.",
      });
    } catch (error) {
      console.error('Error adding skill:', error.message);
      toast({
        title: "Failed to add skill",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleEditSkill = (skill) => {
    setEditMode(true);
    setCurrentSkill(skill);
    setNewSkillName(skill.skill_name);
    setProficiencyLevel(skill.proficiency_level);
    setSkillYearsExperience(skill.years_of_experience?.toString() || '');
    
    // Scroll to top of the page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleUpdateSkill = async () => {
    if (!currentSkill) return;

    try {
      const { error } = await supabase
        .from('job_seeker_skills')
        .update({
          skill_name: newSkillName,
          proficiency_level: proficiencyLevel,
          years_of_experience: parseInt(skillYearsExperience) || 0,
        })
        .eq('id', currentSkill.id);

      if (error) throw error;

      resetForm();
      fetchUserSkills();

      toast({
        title: "Skill updated",
        description: "Your skill has been successfully updated.",
      });
    } catch (error) {
      console.error('Error updating skill:', error.message);
      toast({
        title: "Failed to update skill",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteSkill = async (id) => {
    try {
      const { error } = await supabase
        .from('job_seeker_skills')
        .delete()
        .eq('id', id);

      if (error) throw error;

      fetchUserSkills();

      toast({
        title: "Skill deleted",
        description: "Your skill has been successfully deleted.",
      });
    } catch (error) {
      console.error('Error deleting skill:', error.message);
      toast({
        title: "Failed to delete skill",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const renderSkillForm = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="skillName">Skill Name*</Label>
        <Input
          id="skillName"
          placeholder="Enter skill name"
          value={newSkillName}
          onChange={(e) => setNewSkillName(e.target.value)}
          className="border-[#0EA5E9]/30"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="proficiencyLevel">Proficiency Level*</Label>
        <Select value={proficiencyLevel} onValueChange={setProficiencyLevel}>
          <SelectTrigger className="border-[#0EA5E9]/30">
            <SelectValue placeholder="Select proficiency level" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="Beginner">Beginner</SelectItem>
            <SelectItem value="Intermediate">Intermediate</SelectItem>
            <SelectItem value="Advanced">Advanced</SelectItem>
            <SelectItem value="Expert">Expert</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="yearsExperience">Years of Experience</Label>
        <Input
          id="yearsExperience"
          type="number"
          placeholder="e.g., 2"
          value={skillYearsExperience}
          onChange={(e) => setSkillYearsExperience(e.target.value)}
          className="border-[#0EA5E9]/30"
        />
      </div>

      <div className="flex space-x-2">
        {editMode ? (
          <>
            <Button 
              type="button" 
              className="bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90 flex-1"
              onClick={handleUpdateSkill}
            >
              Update Skill
            </Button>
            <Button 
              type="button" 
              variant="outline"
              className="border-[#0EA5E9]/30 text-gray-700 hover:bg-gray-100 flex-1"
              onClick={resetForm}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button 
            type="button" 
            className="bg-[#0EA5E9] text-white hover:bg-[#0EA5E9]/90 w-full"
            onClick={handleAddSkill}
          >
            Add Skill
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Card className="border border-[#0EA5E9]/20 shadow-md bg-white/80">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5 text-[#0EA5E9]" />
          Skills
        </CardTitle>
        <CardDescription>
          Add your skills and proficiency levels
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Show edit form at top when in edit mode */}
        {editMode && (
          <div className="border-b border-[#0EA5E9]/20 pb-6 mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">
              Edit Skill
            </h3>
            {renderSkillForm()}
          </div>
        )}
        
        {userSkills.length > 0 ? (
          <div className="space-y-4 mb-8">
            <h3 className="text-lg font-medium text-gray-800">Your Skills</h3>
            <div className="space-y-4">
              {userSkills.map((userSkill) => (
                <div key={userSkill.id} className="p-4 bg-white border border-[#0EA5E9]/30 rounded-md">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-800">
                        {userSkill.skill_name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        Proficiency: {userSkill.proficiency_level}
                      </p>
                      {userSkill.years_of_experience > 0 && (
                        <p className="text-sm text-gray-600">
                          Years of Experience: {userSkill.years_of_experience}
                        </p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-gray-600 hover:text-gray-800 hover:bg-blue-500/20 border-[#0EA5E9]/30"
                        onClick={() => handleEditSkill(userSkill)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-gray-600 hover:text-gray-800 hover:bg-red-500/20 border-[#0EA5E9]/30"
                        onClick={() => handleDeleteSkill(userSkill.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-6 mb-8">
            <p className="text-gray-600">You haven't added any skills yet</p>
          </div>
        )}

        {/* Only show add form at bottom when not in edit mode */}
        {!editMode && (
          <div className="border-t border-[#0EA5E9]/20 pt-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4">
              Add a New Skill
            </h3>
            {renderSkillForm()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SkillsTab;
